import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NSNavbar from '../../components/NSNavbar';
import NSFooter from '../../components/NSFooter';
import PageWrap from '../../components/PageWrap';
import useDocumentMeta from '../../hooks/useDocumentMeta';
import routeMetaFor from '../../utils/routeMeta';
import { brandPath, parentRouteHref } from '../../utils/brandRouting';
import { nsfaqs } from '../../data/faq';
import { SUPPORT_EMAIL } from '../../data/siteContent';

function useNSTheme() {
  useEffect(() => {
    document.body.setAttribute('data-brand', 'nutriscan');
    return () => document.body.removeAttribute('data-brand');
  }, []);
}

function NSFaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`ns-faq-item${open ? ' open' : ''}`} data-testid={`ns-faq-item-${idx}`}>
      <div className="ns-faq-q" onClick={() => setOpen(o => !o)}>
        <span>{q}</span>
        <div className="icon"><i className="fas fa-plus"></i></div>
      </div>
      <div className="ns-faq-a"><p>{a}</p></div>
    </div>
  );
}

export default function NSFAQ() {
  useNSTheme();
  // On nutriscan.tickleapps.com the app route table owns "/", so the parent
  // Support page must be reached by its full parent-site URL.
  const contactHref = parentRouteHref('/contact');
  useDocumentMeta(routeMetaFor('/apps/nutriscan/faq'));

  return (
    <PageWrap testId="ns-faq-page">
      <NSNavbar />

      <section className="ns-hero" style={{ minHeight: 'auto', paddingTop: 'calc(var(--nav-h) + 80px)', paddingBottom: 64 }}>
        <div className="ns-hero-leaf-1"></div>
        <div className="ns-hero-leaf-2"></div>
        <div className="ns-hero-grid-pattern"></div>
        <div className="wrap" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <div className="eyebrow reveal" style={{ justifyContent: 'center', color: '#86EFAC' }}>FAQ</div>
          <h1 className="reveal d1" style={{ color: '#fff' }}>Questions about NutriScan.</h1>
          <p className="lead reveal d2" style={{ color: 'rgba(255,255,255,0.7)', margin: '12px auto 0', maxWidth: 540 }}>
            Everything you might want to know about scanning, scoring, privacy, and how the app works under the hood.
          </p>
        </div>
      </section>

      <section className="section" style={{ background: 'var(--ns-bg)' }}>
        <div className="wrap-sm">
          <div className="reveal" data-testid="ns-faq-list">
            {nsfaqs.map((f, i) => <NSFaqItem key={i} idx={i} {...f} />)}
          </div>

          <div className="reveal" style={{ marginTop: 48, background: 'var(--ns-surf)', border: '1px solid var(--ns-border)', borderRadius: 20, padding: '32px 28px', textAlign: 'center' }}>
            <h3 style={{ fontSize: '1.1rem' }}>Couldn't find your answer?</h3>
            <p style={{ marginTop: 8, marginBottom: 20 }}>Contact support and tell us which app you use.</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="btn btn-ns" data-testid="ns-faq-email">
                <i className="fas fa-envelope"></i> Email support
              </a>
              {contactHref
                ? <a href={contactHref} className="btn btn-ns-outline" data-testid="ns-faq-contact">Contact form</a>
                : <Link to="/contact" className="btn btn-ns-outline" data-testid="ns-faq-contact">Contact form</Link>}
            </div>
          </div>

          <div className="reveal" style={{ marginTop: 32, textAlign: 'center', fontSize: '0.85rem', color: 'var(--ns-muted)' }}>
            See also: <Link to={brandPath('nutriscan', '/privacy')} style={{ color: 'var(--ns-green-text)', fontWeight: 600 }}>Privacy Policy</Link> · <Link to={brandPath('nutriscan', '/terms')} style={{ color: 'var(--ns-green-text)', fontWeight: 600 }}>Terms of Service</Link> · <Link to={brandPath('nutriscan', '/account-deletion')} style={{ color: 'var(--ns-green-text)', fontWeight: 600 }}>Account Deletion</Link>
          </div>
        </div>
      </section>

      <NSFooter />
    </PageWrap>
  );
}
